import React from 'react';
import {
  Box, Paper, Typography, Pagination as MuiPagination,
} from '@mui/material';
import { JournalEntry, Pagination as PaginationType, KpiData, PeriodStatus, SortField, Order } from '../types';
import { DEFAULT_PAGE_LIMIT } from '../constants';
import { ColumnDef } from '../hooks/useColumnVisibility';
import { fmtSum } from '../utils';
import HeaderCell from './HeaderCell';
import SkeletonRow from './SkeletonRow';
import JournalTableRow from './JournalTableRow';
import EmptyState from './shared/EmptyState';

interface JournalTableProps {
  entries: JournalEntry[];
  tableLoading: boolean;
  pagination: PaginationType | null;
  kpi: KpiData | null;
  page: number;
  orderBy: SortField;
  order: Order;
  onSort: (field: SortField) => void;
  onPageChange: (page: number) => void;
  filterParams: Record<string, string>;
  periodStatus: PeriodStatus | null;
  gridTemplate: string;
  visibleDefs: ColumnDef[];
  onEdit: (entry: JournalEntry) => void;
  onDelete: (entry: JournalEntry) => void;
}

const SORT_FIELD_MAP: Partial<Record<string, SortField>> = {
  name: 'materialName',
  producer: 'producer' as SortField,
  serial: 'serialNumber',
  expiry: 'expiryDate',
  qty: 'quantity',
  qtyPcs: 'quantityPcs',
  price: 'price',
  totalSum: 'totalSum',
  recordDate: 'recordDate',
  rxotc: 'rxotc',
};

const JournalTable: React.FC<JournalTableProps> = ({
  entries, tableLoading, pagination, kpi, page,
  orderBy, order, onSort, onPageChange,
  filterParams, periodStatus, gridTemplate, visibleDefs,
  onEdit, onDelete,
}) => {
  const getEmptyMessage = () => {
    if (Object.keys(filterParams).length > 0) return 'Нет записей по выбранным фильтрам';
    return 'Журнал пуст - добавьте первую запись';
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
      <Box sx={{
        overflowX: 'auto', maxHeight: '60vh', overflowY: 'auto',
        '&::-webkit-scrollbar': { height: 6, width: 6 },
        '&::-webkit-scrollbar-thumb': { borderRadius: 3, bgcolor: 'grey.300' },
      }}>
        <Box sx={{ minWidth: 'max-content' }}>
          {/* ── Заголовок ── */}
          <Box sx={{
            display: 'grid', gridTemplateColumns: gridTemplate,
            gap: 1, px: 2, py: 1,
            borderBottom: '1px solid', borderColor: 'divider',
            bgcolor: 'grey.50', alignItems: 'center',
            position: 'sticky', top: 0, zIndex: 10,
          }}>
            {visibleDefs.map(col => {
              const sortField = SORT_FIELD_MAP[col.id];
              const isRight = ['qty', 'qtyPcs', 'price', 'totalSum'].includes(col.id);
              return (
                <Box key={col.id} sx={{ display: 'flex', alignItems: 'center' }}>
                  <HeaderCell
                    orderBy={orderBy}
                    order={order}
                    sortField={sortField}
                    onSort={sortField ? onSort : undefined}
                    align={isRight ? 'right' : 'left'}
                  >
                    {col.label}
                  </HeaderCell>
                </Box>
              );
            })}
          </Box>

          {/* ── Строки ── */}
          {tableLoading
            ? Array.from({ length: 10 }).map((_, i) => (
              <SkeletonRow key={i} colCount={visibleDefs.length} gridTemplate={gridTemplate} />
            ))
            : entries.length === 0
              ? <EmptyState message={getEmptyMessage()} />
              : entries.map((row, idx) => (
                <JournalTableRow
                  key={row.id ?? idx}
                  row={row}
                  globalIdx={(page - 1) * DEFAULT_PAGE_LIMIT + idx + 1}
                  visibleDefs={visibleDefs}
                  gridTemplate={gridTemplate}
                  periodStatus={periodStatus}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))
          }
        </Box>
      </Box>

      {/* ── Футер с пагинацией ── */}
      {!tableLoading && pagination && pagination.totalCount > 0 && (
        <Box sx={{
          px: 2, py: 1.5,
          borderTop: '1px solid', borderColor: 'divider',
          bgcolor: 'grey.50',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: 1,
        }}>
          <Typography variant="body2" color="text.secondary">
            Записей: <strong>{pagination.totalCount.toLocaleString('ru-RU')}</strong>
            {' · '}Страница <strong>{pagination.page}</strong> из <strong>{pagination.totalPages}</strong>
          </Typography>
          <MuiPagination
            count={pagination.totalPages}
            page={page}
            onChange={(_, v) => onPageChange(v)}
            size="small"
            color="primary"
            showFirstButton
            showLastButton
          />
          <Typography variant="body2" color="text.secondary">
            Итого: <strong>{fmtSum(kpi?.totalSum ?? 0)}</strong>
            {' · '}
            <strong>
              {kpi?.totalQty.toLocaleString('ru-RU')} уп.
              {(kpi?.totalQtyPcs ?? 0) > 0 && ` / ${kpi?.totalQtyPcs.toLocaleString('ru-RU')} шт.`}
            </strong>
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default JournalTable;
